import { DOCUMENT_TAXONOMY_AI_DESCRIPTIONS } from "@common/documentCategoryAIDescriptions";
import { DocumentTaxonomy } from "@common/documentCategories";
import { DocumentCategory } from "@common/types/documentCategory";
import { FiltersTypes } from "@common/types/filters/general";

type DocumentCategorySearchable = Pick<
    DocumentCategory,
    "label" | "description" | "documentTaxonomy"
>;

function isDocumentCategoryMatchingSearch(
    category: DocumentCategorySearchable,
    search: string,
): boolean {
    const taxonomyDescription =
        DOCUMENT_TAXONOMY_AI_DESCRIPTIONS[category.documentTaxonomy];

    return (
        taxonomyDescription.label
            .toLowerCase()
            .includes(search.toLowerCase()) ||
        taxonomyDescription.description
            .toLowerCase()
            .includes(search.toLowerCase())
    );
}

export function matchesDocumentCategorySearch(
    category: DocumentCategorySearchable,
    search: string,
): boolean {
    if (search.length === 0) {
        return true;
    }

    return (
        category.label.toLowerCase().includes(search.toLowerCase()) ||
        (category.description ?? "")
            .toLowerCase()
            .includes(search.toLowerCase()) ||
        isDocumentCategoryMatchingSearch(category, search)
    );
}

// ─── ViewTable row type ───────────────────────────────────────────────────────

export interface DocumentCategoryRow {
    id: string;
    parentId: string | null;
    label: string;
    description: string | null;
    type: DocumentCategoryFlattenedItemType;
    category: DocumentCategory;
    documentTaxonomy: DocumentTaxonomy;
    isEditable: boolean;
    // stats (populated after data fetch)
    percentage: number;
    groupPercentage: number;
    documentsCount: number;
    groupDocumentsCount: number;
    children?: DocumentCategoryRow[];
}

export enum DocumentCategoryFlattenedItemType {
    Category = "category",
    CustomCategory = "customCategory",
}

export interface DocumentCategoryStatMap {
    count: number;
    groupCount: number;
}

function matchesSearch(text: string, search: string): boolean {
    return text.toLowerCase().includes(search.toLowerCase());
}

function matchesFilter(
    category: DocumentCategory,
    filters: Pick<FiltersTypes, "documentCategoryIds">,
): boolean {
    if (!filters.documentCategoryIds?.[0]) {
        return true;
    }
    return filters.documentCategoryIds[0] === category.id;
}

function matchesChildFilter(
    child: DocumentCategory,
    filters: Pick<FiltersTypes, "documentCategoryIds">,
): boolean {
    if (!filters.documentCategoryIds?.[0]) {
        return true;
    }
    return filters.documentCategoryIds[0] === child.parentCategoryId;
}

export interface BuildDocumentCategoryRowsOptions {
    search: string;
    filters: Pick<FiltersTypes, "documentCategoryIds">;
    statsMap: Map<string, DocumentCategoryStatMap>;
    total: number;
}

export function buildDocumentCategoryRows(
    documentCategories: DocumentCategory[],
    options: BuildDocumentCategoryRowsOptions,
): DocumentCategoryRow[] {
    const { search, filters, statsMap, total } = options;
    const rows: DocumentCategoryRow[] = [];

    for (const category of documentCategories) {
        const children: DocumentCategoryRow[] = (category.subcategories ?? [])
            .filter((child: DocumentCategory) => {
                if (!matchesChildFilter(child, filters)) {
                    return false;
                }
                if (search.length === 0) {
                    return true;
                }
                return (
                    matchesSearch(child.label, search) ||
                    matchesSearch(child.description ?? "", search)
                );
            })
            .map((child: DocumentCategory): DocumentCategoryRow => ({
                id: child.id,
                parentId: category.id,
                label: child.label,
                description: child.description ?? null,
                type: child.workspaceId
                    ? DocumentCategoryFlattenedItemType.CustomCategory
                    : DocumentCategoryFlattenedItemType.Category,
                category: child,
                documentTaxonomy: category.documentTaxonomy,
                isEditable: Boolean(child.workspaceId),
                percentage:
                    total === 0
                        ? 0
                        : ((statsMap.get(child.id)?.count ?? 0) / total) * 100,
                groupPercentage:
                    total === 0
                        ? 0
                        : ((statsMap.get(child.id)?.groupCount ?? 0) / total) *
                          100,
                documentsCount: statsMap.get(child.id)?.count ?? 0,
                groupDocumentsCount: statsMap.get(child.id)?.groupCount ?? 0,
                children: [],
            }));

        const parentMatchesSearch =
            search.length === 0 ||
            matchesSearch(category.label, search) ||
            matchesSearch(category.description ?? "", search) ||
            children.length > 0;

        const parentMatchesFilter = matchesFilter(category, filters);

        if (!parentMatchesSearch || !parentMatchesFilter) {
            continue;
        }

        const directCount = statsMap.get(category.id)?.count ?? 0;
        const groupDocumentsCount =
            directCount +
            (category.subcategories ?? []).reduce(
                (acc, subcategory) =>
                    acc + (statsMap.get(subcategory.id)?.count ?? 0),
                0,
            );

        rows.push({
            id: category.id,
            parentId: category.parentCategoryId,
            label: category.label,
            description: category.description ?? null,
            type: DocumentCategoryFlattenedItemType.Category,
            category,
            documentTaxonomy: category.documentTaxonomy,
            isEditable: false,
            percentage: total === 0 ? 0 : (directCount / total) * 100,
            groupPercentage:
                total === 0 ? 0 : (groupDocumentsCount / total) * 100,
            documentsCount: directCount,
            groupDocumentsCount,
            children,
        });
    }

    return rows;
}

export { getDefaultExpandedState } from "@design-system/ViewTable/core/getDefaultExpandedState";
